import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Hash,
  Wrench,
  Table,
  Braces,
  Binary,
  KeyRound,
  PenLine,
  Fingerprint,
  Clock,
  Link2,
  ShieldCheck,
  Palette,
  Regex,
  FileText,
  Search,
  Eye,
  Timer,
  Box,
  Send,
} from "lucide-react";

type Category = "all" | "ai" | "encoding" | "crypto" | "text" | "dev";

const CATEGORIES: { key: Category; label: string }[] = [
  { key: "all", label: "All" },
  { key: "ai", label: "AI" },
  { key: "encoding", label: "Encoding" },
  { key: "crypto", label: "Crypto" },
  { key: "text", label: "Text" },
  { key: "dev", label: "Dev" },
];

const TOOLS: {
  slug: string;
  name: string;
  description: string;
  icon: typeof Hash;
  category: Category;
}[] = [
    {
      slug: "llm-token-counter",
      name: "LLM Token Counter",
      description:
        "Count tokens for GPT-5.2, Claude Opus 4.6, Gemini and more. See cost estimates instantly.",
      icon: Hash,
      category: "ai",
    },
    {
      slug: "ai-model-comparison",
      name: "AI Model Comparison",
      description:
        "Compare pricing, context windows, and capabilities across GPT-5.2, Claude 4.6, Gemini 3.1, Grok 4, Llama 4 and 30+ more.",
      icon: Table,
      category: "ai",
    },
    {
      slug: "json-formatter",
      name: "JSON Formatter & Validator",
      description:
        "Beautify, minify, or validate JSON. Tree view, error highlighting with line numbers.",
      icon: Braces,
      category: "text",
    },
    {
      slug: "base64",
      name: "Base64 Encode / Decode",
      description:
        "Convert text to Base64 and back. Upload files, preview images. Bidirectional.",
      icon: Binary,
      category: "encoding",
    },
    {
      slug: "jwt-decoder",
      name: "JWT Decoder",
      description:
        "Decode JSON Web Tokens. See header, payload, expiry status, and security details.",
      icon: KeyRound,
      category: "crypto",
    },
    {
      slug: "jwt-encoder",
      name: "JWT Encoder",
      description:
        "Create and sign JWTs with HMAC-SHA256/384/512. Set headers, payloads, and secrets.",
      icon: PenLine,
      category: "crypto",
    },
    {
      slug: "uuid-generator",
      name: "UUID & ULID Generator",
      description:
        "Generate UUID v4 and ULID identifiers. Bulk generation up to 100 at a time.",
      icon: Fingerprint,
      category: "crypto",
    },
    {
      slug: "unix-timestamp",
      name: "Unix Timestamp Converter",
      description:
        "Convert Unix timestamps to dates and back. Live clock, relative time, multiple formats.",
      icon: Clock,
      category: "dev",
    },
    {
      slug: "url-encode-decode",
      name: "URL Encode / Decode",
      description:
        "Encode special characters for URLs or decode percent-encoded strings. Query string parser.",
      icon: Link2,
      category: "encoding",
    },
    {
      slug: "hash-generator",
      name: "Hash Generator",
      description:
        "Generate SHA-256, SHA-384, SHA-512, and SHA-1 hashes from text or files.",
      icon: ShieldCheck,
      category: "crypto",
    },
    {
      slug: "color-converter",
      name: "Color Converter",
      description:
        "Convert between Hex, RGB, and HSL. Visual picker, sliders, Tailwind class output.",
      icon: Palette,
      category: "dev",
    },
    {
      slug: "regex-tester",
      name: "Regex Tester",
      description:
        "Test regular expressions with real-time matching, highlighting, and match details.",
      icon: Regex,
      category: "text",
    },
    {
      slug: "diff-checker",
      name: "Diff Checker",
      description:
        "Compare two texts to see additions, deletions, and unchanged lines. Like Git diff.",
      icon: FileText,
      category: "text",
    },
    {
      slug: "markdown-preview",
      name: "Markdown Preview",
      description:
        "Live markdown editor with instant rendered preview. Supports GitHub Flavored Markdown.",
      icon: Eye,
      category: "text",
    },
    {
      slug: "cron-parser",
      name: "Cron Expression Parser",
      description:
        "Parse cron expressions into human-readable descriptions. See next scheduled run times.",
      icon: Timer,
      category: "dev",
    },
    {
      slug: "box-shadow-generator",
      name: "Box Shadow Generator",
      description:
        "Visual CSS box-shadow builder with sliders, color picker, and multiple layers.",
      icon: Box,
      category: "dev",
    },
    {
      slug: "api-tester",
      name: "API Tester",
      description:
        "Send HTTP requests and inspect responses. Supports GET, POST, PUT, DELETE with headers and body.",
      icon: Send,
      category: "dev",
    },
  ];

export function Tools() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<Category>("all");

  useEffect(() => {
    document.title = "Developer Tools — Free Online Tools for Developers — Ballah Code";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `${TOOLS.length} free developer tools from Ballah Code. JSON formatter, Base64 encoder, JWT decoder & encoder, regex tester, hash generator, markdown preview, cron parser and more. All run in your browser.`,
      );
    }
  }, []);

  const filtered = useMemo(() => {
    return TOOLS.filter((tool) => {
      if (category !== "all" && tool.category !== category) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          tool.name.toLowerCase().includes(q) ||
          tool.description.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [search, category]);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-[18px]">
        <Link
          to="/"
          className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors"
        >
          ballah code
        </Link>
        <div className="flex items-center gap-6 text-[13px] text-zinc-600">
          <Link to="/tools" className="text-zinc-300">
            tools
          </Link>
          <Link to="/" className="hover:text-zinc-400 transition-colors">
            home
          </Link>
        </div>
      </nav>

      <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2.5 text-zinc-500 mb-4">
            <Wrench size={18} strokeWidth={1.8} />
            <span className="text-[13px] font-mono">Free tools</span>
          </div>
          <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">
            Developer Tools
          </h1>
          <p className="text-zinc-500 text-[15px] leading-relaxed max-w-lg">
            {TOOLS.length} free tools for developers. JSON, Base64, JWT, regex,
            hashing, and more. All run in your browser.
          </p>
        </div>

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search
              size={14}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tools..."
              className="w-full bg-[#111113] border border-white/[0.06] rounded-lg pl-9 pr-4 py-2.5 text-[13px] text-zinc-300 outline-none placeholder:text-zinc-700 focus:border-white/[0.12] transition-colors"
            />
          </div>
          <div className="flex rounded-lg border border-white/[0.06] overflow-hidden">
            {CATEGORIES.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setCategory(key)}
                className={`px-3 py-2 text-[12px] transition-colors ${category === key
                  ? "bg-white/[0.08] text-zinc-200"
                  : "text-zinc-600 hover:text-zinc-400"
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tool cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((tool) => (
            <Link
              key={tool.slug}
              to={`/tools/${tool.slug}`}
              className="group bg-[#111113] border border-white/[0.06] rounded-xl p-5 hover:border-white/[0.12] hover:bg-[#141416] transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-lg bg-white/[0.04] flex items-center justify-center text-zinc-400 group-hover:text-zinc-200 transition-colors">
                  <tool.icon size={18} strokeWidth={1.8} />
                </div>
                <ArrowRight
                  size={14}
                  className="text-zinc-700 group-hover:text-zinc-400 transition-colors mt-1"
                />
              </div>
              <h2 className="text-[15px] font-medium text-zinc-200 mb-1.5">
                {tool.name}
              </h2>
              <p className="text-[13px] text-zinc-500 leading-relaxed">
                {tool.description}
              </p>
            </Link>
          ))}
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-zinc-600 text-[14px]">
            No tools match "{search}"{category !== "all" ? ` in ${category}` : ""}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
        <Link to="/" className="hover:text-zinc-600 transition-colors">
          ballah code
        </Link>
        <Link to="/tools" className="hover:text-zinc-600 transition-colors">
          tools
        </Link>
        <span>ballah tech</span>
      </footer>
    </div>
  );
}
